/**
 * Shared cooling system list helpers — grouping and table cell display (#996 Phase 2).
 * Mirrors smallUpsListUtils.
 *
 * Phase 2.5: the section-agnostic core (grouping, reorder, session patch,
 * duplicate naming, validation, draft, legacy synthesis) now comes from the
 * shared repeatable-section factory. Cooling-specific table column display
 * stays here. All public names below are unchanged.
 */

import { DEFAULT_COOLING_SYSTEM_CONFIGURATION } from '../../types/coolingSystem';
import {
  generateAutoCoolingSystemName,
  isAutoGeneratedCoolingSystemName,
} from '../../utils/coolingSystemAutoName';
import {
  buildRepeatableNameSubline,
  filterRepeatableLocationTagTableColumns,
  formatRepeatableLocationTagColumnHeader,
  isNativeCompactLayout,
  repeatableNameColumnUsesDoubleLine,
  type RepeatableLocationTagColumnOptions,
} from '../../utils/locationTagListDisplay';
import {
  createRepeatableSectionListUtils,
  parseOptionalNumber as parseOptionalNumberCore,
} from '../repeatable/repeatableSectionListUtils';

import type {
  CoolingSystemConfiguration,
  GroupedCoolingSystemConfigurations,
  UpdateCoolingSystemConfigurationInput,
} from '../../types/coolingSystem';

/** Desktop web columns (#996). ΔT replaces description (rich HTML / images cannot render in a cell). */
export const COOLING_SYSTEM_TABLE_DESKTOP_COLUMNS = [
  'NAME',
  'ΔT',
  'CAPACITY',
  'REDUNDANCY',
  'EXISTING',
  'ROOM',
] as const;

/**
 * Compact mirrors desktop column order; REDUNDANCY folds away on narrow screens.
 * ROOM (#999 Story 3.4) stays on compact so the location tag stays visible.
 * Desktop: NAME | ΔT | CAPACITY | REDUNDANCY | EXISTING | ROOM
 * Compact: NAME (+redundancy) | CAPACITY | EXISTING | ROOM
 */
export const COOLING_SYSTEM_TABLE_COMPACT_COLUMNS = [
  'NAME',
  'CAPACITY',
  'EXISTING',
  'ROOM',
] as const;

/**
 * Compact skeleton (#999 Option B) — uniform 4-column layout shared by the compact
 * layout on native mobile AND narrow/mobile web. EXISTING is dropped (it collides
 * with the EXISTING/NEW status pill) and ΔT is the metric middle column;
 * redundancy relocates under CAPACITY. Wide desktop web keeps
 * {@link COOLING_SYSTEM_TABLE_DESKTOP_COLUMNS}; {@link COOLING_SYSTEM_TABLE_COMPACT_COLUMNS}
 * is retained only for the legacy type union.
 * Compact: NAME (+pill) | ΔT | CAPACITY/KW (+redundancy) | ROOM
 */
export const COOLING_SYSTEM_TABLE_NATIVE_COMPACT_COLUMNS = [
  'NAME',
  'ΔT',
  'CAPACITY',
  'ROOM',
] as const;

/** All column keys (superset for typing and legacy imports). */
export const COOLING_SYSTEM_TABLE_COLUMNS =
  COOLING_SYSTEM_TABLE_DESKTOP_COLUMNS;

export type CoolingSystemTableLayout = 'desktop' | 'compact';

/** Injected only when `showStatusColumn` is set (Devices view). */
export const COOLING_SYSTEM_TABLE_STATUS_COLUMN = 'STATUS' as const;

export type CoolingSystemTableColumnKey =
  | (typeof COOLING_SYSTEM_TABLE_DESKTOP_COLUMNS)[number]
  | (typeof COOLING_SYSTEM_TABLE_COMPACT_COLUMNS)[number]
  | (typeof COOLING_SYSTEM_TABLE_NATIVE_COMPACT_COLUMNS)[number]
  | typeof COOLING_SYSTEM_TABLE_STATUS_COLUMN;

export const COOLING_SYSTEM_TABLE_COLUMN_ALIGN: Record<
  CoolingSystemTableColumnKey,
  'left' | 'center'
> = {
  NAME: 'left',
  STATUS: 'left',
  ΔT: 'center',
  CAPACITY: 'center',
  REDUNDANCY: 'center',
  EXISTING: 'center',
  ROOM: 'left',
};

export type CoolingSystemListSection = 'existing' | 'new';

// ---------------------------------------------------------------------------
// Section-agnostic core (delegated to the shared repeatable-section factory)
// ---------------------------------------------------------------------------

const core = createRepeatableSectionListUtils<
  CoolingSystemConfiguration,
  UpdateCoolingSystemConfigurationInput
>({
  defaultConfiguration: DEFAULT_COOLING_SYSTEM_CONFIGURATION,
  generateAutoName: generateAutoCoolingSystemName,
  isAutoGeneratedName: isAutoGeneratedCoolingSystemName,
  namePrefix: 'Cooling System ',
  newLabel: 'New cooling system',
  nameNoun: 'Cooling System',
  getDisplayNameFallback: unit => unit.coolingDescription?.trim() || undefined,
  legacySourceKeys: [
    // #1149 Issue 4: only genuine unit-property fields may TRIGGER a synthesized
    // fallback unit. Readiness answers (existing-system-onsite, chilled-water,
    // readiness notes) are request-level, not units — keeping them out of this
    // trigger stops a bare readiness answer from conjuring a phantom "0 units
    // added" row. They still SURFACE on a synthesized unit via mapLegacyFields
    // below when real unit data is present (#997 Bug 9).
    'coolingDescription',
    'coolingTotalAvailableCapacityKw',
    'coolingRequiredRedundancyForSystem',
    'coolingTempRisePrefThroughItEquipment',
    'coolingDescribeApproach',
    'coolingDescribeRearDoorLoop',
  ],
  mapLegacyFields: source => ({
    coolingDescription: source.coolingDescription as string | undefined,
    coolingIsExistingSystemOnsite: source.coolingIsExistingSystemOnsite as
      | string
      | undefined,
    coolingTotalAvailableCapacityKw: source.coolingTotalAvailableCapacityKw as
      | string
      | undefined,
    coolingRequiredRedundancyForSystem:
      source.coolingRequiredRedundancyForSystem as string | undefined,
    coolingTempRisePrefThroughItEquipment:
      source.coolingTempRisePrefThroughItEquipment as string | undefined,
    coolingDescribeApproach: source.coolingDescribeApproach as
      | string
      | undefined,
    coolingRearDoorHexDesired: source.coolingRearDoorHexDesired as
      | boolean
      | undefined,
    coolingDescribeRearDoorLoop: source.coolingDescribeRearDoorLoop as
      | string
      | undefined,
    coolingIsChilledWaterAvailable: source.coolingIsChilledWaterAvailable as
      | string
      | undefined,
    coolingAdditionalReadinessInfo: source.coolingAdditionalReadinessInfo as
      | string
      | undefined,
  }),
});

export function normalizeCoolingSystemStatus(
  status: CoolingSystemConfiguration['status'] | string | undefined,
): CoolingSystemConfiguration['status'] {
  return core.normalizeStatus(status);
}

export function groupCoolingSystemsByStatus(
  units: CoolingSystemConfiguration[],
): GroupedCoolingSystemConfigurations {
  return core.groupByStatus(units);
}

export function sortCoolingSystemsByDisplayOrder(
  units: CoolingSystemConfiguration[],
): CoolingSystemConfiguration[] {
  return core.sortByDisplayOrder(units);
}

/** Reorder units within EXISTING or NEW section; reassigns global displayOrder. */
export function applyCoolingSystemSectionReorder(
  sortedUnits: CoolingSystemConfiguration[],
  section: CoolingSystemListSection,
  fromIndex: number,
  toIndex: number,
): CoolingSystemConfiguration[] {
  return core.applySectionReorder(sortedUnits, section, fromIndex, toIndex);
}

/** Merge editor save payload onto a session unit without wiping fields with `undefined`. */
export function applyCoolingSystemSessionPatch(
  unit: CoolingSystemConfiguration,
  patch: UpdateCoolingSystemConfigurationInput,
): CoolingSystemConfiguration {
  return core.applySessionPatch(unit, patch);
}

export function coolingSystemIdsEqual(
  left: number | undefined,
  right: number | undefined,
): boolean {
  return core.idsEqual(left, right);
}

export function getCoolingSystemDescriptionDisplay(
  unit: CoolingSystemConfiguration,
): string {
  return unit.coolingDescription?.trim() || '—';
}

/** NAME column primary — user-defined name, else "Cooling System N" by display order. */
export function getCoolingSystemNamePrimaryDisplay(
  unit: CoolingSystemConfiguration,
): string {
  return core.getNamePrimaryDisplay(unit);
}

/** Editor chrome title — user name, else "Cooling System N", else "New cooling system". */
export function getCoolingSystemEditChromeTitle(
  name: string | undefined,
  options: {
    isNew?: boolean;
    displayOrder?: number;
    positionIndex?: number;
  } = {},
): string {
  return core.getEditChromeTitle(name, options);
}

export function formatCoolingSystemTableCell(
  unit: CoolingSystemConfiguration,
  column: CoolingSystemTableColumnKey,
): string {
  switch (column) {
    case 'NAME':
      return getCoolingSystemNamePrimaryDisplay(unit);
    case 'ΔT':
      return unit.coolingTempRisePrefThroughItEquipment?.trim() || '—';
    case 'CAPACITY':
      return unit.coolingTotalAvailableCapacityKw?.trim() || '—';
    case 'REDUNDANCY':
      return unit.coolingRequiredRedundancyForSystem?.trim() || '—';
    case 'EXISTING':
      return unit.coolingIsExistingSystemOnsite?.trim() || '—';
    case 'ROOM':
      // Rendered via LocationRoomCell in the row; text unused.
      return '—';
    case 'STATUS':
      // Rendered via RepeatableUnitStatusPill in the row.
      return '';
    default:
      return '—';
  }
}

export function coolingSystemTableColumnsForLayout(
  layout: CoolingSystemTableLayout,
  options?: RepeatableLocationTagColumnOptions,
): readonly CoolingSystemTableColumnKey[] {
  const columns =
    layout === 'compact'
      ? COOLING_SYSTEM_TABLE_NATIVE_COMPACT_COLUMNS
      : COOLING_SYSTEM_TABLE_DESKTOP_COLUMNS;
  return filterRepeatableLocationTagTableColumns(columns, options, layout);
}

export function coolingSystemTableHeaderLabel(
  column: CoolingSystemTableColumnKey,
  layout: CoolingSystemTableLayout,
): string {
  if (layout === 'compact') {
    if (column === 'CAPACITY') {
      return formatRepeatableLocationTagColumnHeader(column, 'KW');
    }
  }
  return formatRepeatableLocationTagColumnHeader(column, column);
}

/** DESCRIPTION subline — cooling approach when set. */
export function getCoolingSystemApproachSubline(
  unit: CoolingSystemConfiguration,
): string | undefined {
  const approach = unit.coolingDescribeApproach?.trim();
  return approach || undefined;
}

/** NAME subline (compact) — required redundancy. */
export function getCoolingSystemRedundancySubline(
  unit: CoolingSystemConfiguration,
): string | undefined {
  const redundancy = unit.coolingRequiredRedundancyForSystem?.trim();
  return redundancy || undefined;
}

export function getCoolingSystemColumnSubline(
  column: CoolingSystemTableColumnKey,
  unit: CoolingSystemConfiguration,
  layout: CoolingSystemTableLayout,
  options?: { locationTagName?: string | null },
): string | undefined {
  // Native compact (#999 Option B): STATUS pill folds under NAME, ΔT is
  // a metric column (no subline), and redundancy relocates (bare) under the
  // trailing CAPACITY/KW cell.
  if (isNativeCompactLayout(layout)) {
    if (column === 'CAPACITY') {
      return getCoolingSystemRedundancySubline(unit);
    }
    return undefined;
  }
  if (column === 'NAME') {
    return buildRepeatableNameSubline(layout, {
      compactParts:
        layout === 'compact'
          ? [getCoolingSystemRedundancySubline(unit)]
          : undefined,
      locationTagName: options?.locationTagName,
    });
  }
  return undefined;
}

export function coolingSystemColumnUsesDoubleLine(
  column: CoolingSystemTableColumnKey,
  layout: CoolingSystemTableLayout = 'desktop',
  options?: { locationTagName?: string | null },
): boolean {
  if (isNativeCompactLayout(layout)) {
    // NAME is single-line (pill renders beneath it in the row); only the trailing
    // CAPACITY cell double-lines (redundancy). ΔT stays single-line.
    return column === 'CAPACITY';
  }
  if (column === 'NAME') {
    return repeatableNameColumnUsesDoubleLine(layout, options?.locationTagName);
  }
  return false;
}

/**
 * Primary list title — user-defined name preferred, then description, then
 * "Cooling System N" when displayOrder is available. Returns null when no suitable name.
 */
export function getCoolingSystemDisplayName(
  unit: CoolingSystemConfiguration,
): string | null {
  return core.getDisplayName(unit);
}

/** Unique duplicate name — user names get `-copy`; unnamed/auto names get a fresh serial. */
export function buildDuplicateCoolingSystemName(
  sourceName: string | undefined,
  takenNames: Iterable<string | undefined> = [],
  tempId: number,
): string {
  return core.buildDuplicateName(sourceName, takenNames, tempId);
}

/** Assign a serial name when the user did not provide one. */
export function ensureCoolingSystemNameForCreate(
  data: UpdateCoolingSystemConfigurationInput,
  tempId: number,
  takenNames: Iterable<string | undefined> = [],
): UpdateCoolingSystemConfigurationInput {
  return core.ensureNameForCreate(data, tempId, takenNames);
}

/** Client-side uniqueness check before persisting cooling system session changes. */
export function validateSessionCoolingSystemNames(
  session: CoolingSystemConfiguration[],
): string | null {
  return core.validateSessionNames(session);
}

/** Clone fields for duplicate flow (no id/metadata; keeps source existing/new status). */
export function cloneCoolingSystemForCreate(
  source: CoolingSystemConfiguration,
  takenNames: Iterable<string | undefined> = [],
  tempId: number,
): Partial<CoolingSystemConfiguration> {
  return core.cloneForCreate(source, takenNames, tempId);
}

/** Parse numeric form input — returns undefined for empty or non-finite values. */
export const parseOptionalNumber = parseOptionalNumberCore;

export function createDraftCoolingSystemConfiguration(
  data: UpdateCoolingSystemConfigurationInput,
  displayOrder: number,
  tempId: number,
): CoolingSystemConfiguration {
  return core.createDraftConfiguration(data, displayOrder, tempId);
}

/** Client-only id for a unit synthesized from legacy flat request columns. */
export const LEGACY_COOLING_SYSTEM_SYNTHETIC_ID = core.SYNTHETIC_ID;

/** Whether a list row may offer delete (draft rows or persisted id > 0, not legacy synthetic). */
export function canDeleteCoolingSystemFromList(
  unit: CoolingSystemConfiguration,
  isDraftMode: boolean,
): boolean {
  return core.canDeleteFromList(unit, isDraftMode);
}

export function hasLegacyCoolingSystemRequestFields(
  source: Record<string, unknown>,
): boolean {
  return core.hasLegacyRequestFields(source);
}

/** Build one display row from pre–multi-instance flat request fields when child rows are absent. */
export function buildLegacyCoolingSystemFromRequestFields(
  source: Record<string, unknown>,
  requestId: string | number,
): CoolingSystemConfiguration | null {
  return core.buildLegacyFromRequestFields(source, requestId);
}
